import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Heading, Dropdown, Container, Box, Flex, Text, Input } from "samba-ui";
import styled from "styled-components";
import PageLoader from "./PageLoader";
import {
  loadAdminDashboards,
  addNewAdminDashboard,
  // searchDashboards,
  updateSelectedPage
} from "../redux/dashboards";
import { getAdminDashboardCampaigns, getAdminDashboardCreators } from "../api";
import DashboardList from "./DashboardList";
import { DASHBOARD_DROPDOWN_OPTIONS } from "../constants";
import serialize from "../utils/serialize";
import useQuery from "./hooks/useQuery";

const TYPE_DROPDOWN_VALUE_TO_LABEL = {
  all: "All",
  rf: "Reach & Frequecy",
  oem: "OEM",
  vtr: "vtr",
  bcr: "BCR",
  svtr: "Streaming VTR"
};

const range = (start, stop, step) =>
  Array.from({ length: (stop - start) / step + 1 }, (_, i) => start + i * step);

const StyledInput = styled(Input)`
  width: 100%;
`;

const defaultType = { label: "All", value: "all" };
const defaultCampaign = { label: "All", value: "all" };
const defaultCreator = { label: "All", value: "all" };

const ManageDashboards = ({
  dashboards,
  loadAdminDashboards,
  addNewAdminDashboard,
  updateSelectedPage,
  user,
  history
}) => {
  let query = useQuery();
  const _name = query.get("name");
  const _campaignName = query.get("campaignName");
  const _type = query.get("type");
  const _creator = query.get("creator");

  const [loading, setLoading] = useState(false);
  const [name, setName] = useState(_name || "");
  const [creator, setCreator] = useState(
    _creator ? { label: _creator, value: _creator } : defaultCreator
  );
  const [selectedType, setSelectedType] = useState(
    _type
      ? { label: TYPE_DROPDOWN_VALUE_TO_LABEL[_type], value: _type }
      : defaultType
  );
  const [campaignName, setCampaignName] = useState(
    _campaignName
      ? { label: _campaignName, value: _campaignName }
      : defaultCampaign
  );
  const [campaignOptions, setCampaignOptions] = useState([]);
  const [creatorOptions, setCreatorOptions] = useState([]);
  const paginationOptions = range(1, dashboards.totalPages, 1).map(p => ({
    label: p,
    value: p
  }));

  useEffect(() => {
    const req = async () => {
      setLoading(true);

      let queryParam = {
        page: dashboards.currentPage
      };

      if (selectedType.value !== "all") {
        queryParam["type"] = selectedType.value;
      }

      if (name) {
        queryParam["name"] = name;
      }

      if (campaignName.value !== "all") {
        queryParam["campaignName"] = campaignName.value;
      }

      if (creator.value !== "all") {
        queryParam["creator"] = creator.value;
      }

      const query = serialize(queryParam);
      await loadAdminDashboards(query);
      setLoading(false);
    };
    req();
  }, [
    campaignName.value,
    dashboards.currentPage,
    loadAdminDashboards,
    name,
    creator.value,
    selectedType.value
  ]);

  useEffect(() => {
    const req = async () => {
      const result = await getAdminDashboardCreators();
      let { data } = await result.json();
      data = data
        .sort((a, b) => a.localeCompare(b))
        .map(d => ({ label: d, value: d }));
      data.unshift(defaultCreator);
      setCreatorOptions(data);
    };
    req();
  }, []);

  useEffect(() => {
    const req = async () => {
      const result = await getAdminDashboardCampaigns();
      let { data } = await result.json();
      data = data
        .sort((a, b) => a.localeCompare(b))
        .map(d => ({ label: d, value: d }));
      data.unshift(defaultCampaign);
      setCampaignOptions(data);
    };
    req();
  }, []);

  const onNameChange = e => {
    const { value } = e.target;
    setName(value);
    updateUrl({ _name: value });
  };

  const onTypeSelect = selected => {
    setSelectedType(selected);
    updateUrl({ _type: selected.value });
  };

  const onCampaignSelect = selected => {
    setCampaignName(selected);
    updateUrl({ _campaignName: selected.value });
  };

  const onCreatorSelect = selected => {
    setCreator(selected);
    updateUrl({ _creator: selected.value });
  };

  const updateUrl = ({ _name, _campaignName, _type }) => {
    let params = {};

    if (_name || name) {
      params["name"] = _name || name;
    }

    if (_campaignName || (campaignName && campaignName.value !== "all")) {
      params["campaignName"] = _campaignName || campaignName.value;
    }

    if (_type || (selectedType && selectedType.value !== "all")) {
      params["type"] = _type || selectedType.value;
    }

    if (_creator || (creator && creator.value !== "all")) {
      params["creator"] = _creator || creator.value;
    }

    if (Object.keys(params).length) {
      history.replace({
        search: `?${Object.keys(params)
          .map(key => key + "=" + params[key])
          .join("&")}`
      });
    }
  };

  return (
    <Container maxWidth={2048} minWidth={960}>
      <Flex>
        <Box width={3 / 4} mr={4}>
          <Flex mb={3}>
            <Heading color="secondary" fontSize={3} fontWeight={500}>
              All Dashboards
            </Heading>
            <Flex alignItems="center" mb={3} style={{ marginLeft: "auto" }}>
              <Text>Page</Text>
              <Box width={100} px={2}>
                <Dropdown
                  options={paginationOptions}
                  selected={{
                    label: dashboards.currentPage,
                    value: dashboards.currentPage
                  }}
                  onSelect={updateSelectedPage}
                />
              </Box>
              of {dashboards.totalPages}
            </Flex>
          </Flex>
          {loading ? (
            <PageLoader />
          ) : (
            <DashboardList
              user={user}
              dashboards={dashboards.dashboards}
              addNewAdminDashboard={addNewAdminDashboard}
            />
          )}
        </Box>
        <Box width={1 / 4} pt={5}>
          <Box mb={2}>
            <Text fontWeight={1} fontSize={0} color="palette.ink.light">
              Search by Dashboard Name
            </Text>
          </Box>
          <StyledInput
            placeholder="Dashboard name..."
            value={name}
            onChange={onNameChange}
          />
          <Box mt={3}>
            <Box mb={2}>
              <Text fontWeight={1} fontSize={0} color="palette.ink.light">
                Filter by Dashboard Type
              </Text>
            </Box>
            <Dropdown
              options={DASHBOARD_DROPDOWN_OPTIONS}
              selected={selectedType}
              onSelect={onTypeSelect}
            />
          </Box>
          <Box mt={3}>
            <Box mb={2}>
              <Text fontWeight={1} fontSize={0} color="palette.ink.light">
                Filter by Campaign Name
              </Text>
            </Box>
            <Dropdown
              options={campaignOptions}
              selected={campaignName}
              onSelect={onCampaignSelect}
            />
          </Box>
          <Box mt={3}>
            <Box mb={2}>
              <Text fontWeight={1} fontSize={0} color="palette.ink.light">
                Filter by Creator
              </Text>
            </Box>
            <Dropdown
              options={creatorOptions}
              selected={creator}
              onSelect={onCreatorSelect}
            />
          </Box>
        </Box>
      </Flex>
    </Container>
  );
};

const mapStateToProps = state => ({
  dashboards: state.dashboards
});

const mapDispatchToProps = dispatch => ({
  loadAdminDashboards: query => dispatch(loadAdminDashboards(query)),
  updateSelectedPage: newPage => dispatch(updateSelectedPage(newPage)),
  addNewAdminDashboard: body => dispatch(addNewAdminDashboard(body))
});

ManageDashboards.propTypes = {
  dashboards: PropTypes.object,
  loadAdminDashboards: PropTypes.func,
  addNewAdminDashboard: PropTypes.func,
  user: PropTypes.object,
  updateSelectedPage: PropTypes.func
};

export default connect(mapStateToProps, mapDispatchToProps)(ManageDashboards);
